import type {
  GameOverReason,
  IceServer,
  Phase,
  PlayerId,
  PublicPlayer,
  RoomSnapshot,
} from '@protocol';
import type { ConnectionQuality } from '../rtc/peer';
import type { DetectorStatus } from '../detect/detector';
import type { SocketStatus } from '../net/room-client';

export interface LifeLostFx {
  playerId: PlayerId;
  word: string;
  lives: number;
  showAt: number;
  key: number;
}

export interface GameState {
  snapshot: RoomSnapshot | null;
  yourWords: string[];
  wordsRound: number | null;
  deadWords: string[];
  socket: SocketStatus;
  connection: ConnectionQuality;
  detector: DetectorStatus;
  fx: LifeLostFx | null;
  mediaError: string | null;
  detectorError: string | null;
  hasMedia: boolean;
  iceServers: IceServer[] | null;
}

export const initialState: GameState = {
  snapshot: null,
  yourWords: [],
  wordsRound: null,
  deadWords: [],
  socket: 'connecting',
  connection: 'new',
  detector: 'idle',
  fx: null,
  mediaError: null,
  detectorError: null,
  hasMedia: false,
  iceServers: null,
};

export type GameAction =
  | { type: 'snapshot'; snapshot: RoomSnapshot }
  | { type: 'words'; yourWords: string[]; round: number }
  | { type: 'socket'; status: SocketStatus }
  | { type: 'connection'; quality: ConnectionQuality }
  | { type: 'detector'; status: DetectorStatus }
  | { type: 'dead'; word: string }
  | { type: 'fx'; fx: LifeLostFx | null }
  | { type: 'media-error'; message: string | null }
  | { type: 'detector-error'; message: string | null }
  | { type: 'has-media'; value: boolean }
  | { type: 'ice-servers'; iceServers: IceServer[] };

export function reducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'snapshot': {
      const next = { ...state, snapshot: action.snapshot };
      if (state.wordsRound !== null && action.snapshot.round !== state.wordsRound) {
        next.yourWords = [];
        next.wordsRound = null;
        next.deadWords = [];
        next.fx = null;
      }
      return next;
    }
    case 'words':
      return {
        ...state,
        yourWords: action.yourWords,
        wordsRound: action.round,
        deadWords: [],
      };
    case 'dead':
      return { ...state, deadWords: [...state.deadWords, action.word] };
    case 'socket':
      return { ...state, socket: action.status };
    case 'connection':
      return { ...state, connection: action.quality };
    case 'detector':
      return { ...state, detector: action.status };
    case 'fx':
      return { ...state, fx: action.fx };
    case 'media-error':
      return { ...state, mediaError: action.message };
    case 'detector-error':
      return { ...state, detectorError: action.message };
    case 'has-media':
      return { ...state, hasMedia: action.value };
    case 'ice-servers':
      return { ...state, iceServers: action.iceServers };
  }
}


export const you = (s: GameState): PublicPlayer | undefined =>
  s.snapshot?.players.find((p) => p.isYou);

export const opponent = (s: GameState): PublicPlayer | undefined =>
  s.snapshot?.players.find((p) => !p.isYou);

export const phase = (s: GameState): Phase => s.snapshot?.phase ?? 'waiting';

export const burnedWords = (s: GameState): string[] =>
  s.deadWords.filter((word) => s.yourWords.includes(word));

export function finishingWord(s: GameState): string | null {
  const mine = burnedWords(s);
  return mine[mine.length - 1] ?? null;
}

export const damageReport = (s: GameState): string[] => burnedWords(s);

export function gameOverText(
  s: GameState,
): { title: string; detail: string } | null {
  const snap = s.snapshot;
  if (!snap || snap.phase !== 'gameover') return null;
  const me = you(s);
  const won = snap.winnerId !== null && me?.id === snap.winnerId;
  const reasons: Record<GameOverReason, string> = {
    lives: won ? 'They ran out of hearts.' : 'You ran out of hearts.',
    'forfeit-disconnect': won
      ? 'Your opponent disconnected and did not come back.'
      : 'You were disconnected for too long.',
    'peer-left': won ? 'Your opponent left the game.' : 'You left the game.',
  };
  return {
    title: won ? 'You win' : 'You lose',
    detail: snap.gameOverReason ? reasons[snap.gameOverReason] : '',
  };
}
